import { VuexModule, Module, Action, Mutation } from 'vuex-module-decorators'
import { IGlass } from '@/data/glass'
import store, { IRootState } from '@/store'
import CartItem, { ICartItem } from '@/store/models/CartItem'
import GlassType from '@/data/glass-type'
import GlassOptionValue from '@/data/glass-option-value'
import GlassOption from '@/data/glass-option'
import { BasketType } from '@/data/enums'

export interface ICartState {
  currentItem: ICartItem | undefined
  items: ICartItem[]
  glassType: GlassType | undefined
  sidebarOpen: boolean
}

@Module({
  dynamic: true,
  store,
  name: 'cart',
  namespaced: true
})
class Cart extends VuexModule<any, IRootState> implements ICartState {
  public currentItem: ICartItem | undefined
  public glassType: GlassType | undefined
  public items: ICartItem[] = []
  public sidebarOpen: boolean = false

  public get totalPrice (): number {
    let price = 0.0
    this.items.forEach((item) => {
      price += item.displayPrice
    })
    return price
  }

  public get currentPrice (): number {
    let price = this.totalPrice
    if (this.currentItem) {
      if (this.context.rootState.data) {
        if (this.context.rootState.data.optician.basketType === BasketType.Current) {
          price = this.currentItem.displayPrice
        }
      }
    }
    return price
  }

  @Action
  public clear () {
    this.context.state.items.forEach((cartItem: ICartItem) => {
      if (cartItem.type) {
        cartItem.type.options.forEach((option: GlassOption) => {
          option.values.forEach((optionValue) => {
            optionValue.selected = false
          })
        })
      }
    })
    this.context.commit('CURRENT_ITEM', undefined)
    this.context.commit('SET_ITEMS', [])
  }

  @Action({ commit: 'CURRENT_ITEM' })
  public configureGlasses (glasses: IGlass) {
    let cartItem = this.items.find((item: ICartItem) => item.id === glasses.id)
    if (cartItem === undefined) {
      cartItem = new CartItem(glasses)
      cartItem.id = glasses.id
      this.items.push(cartItem)
    }
    return cartItem
  }

  @Action
  public removeItem (item: ICartItem) {
    if (item.type) {
      item.type.options.forEach((option: GlassOption) => {
        option.values.forEach((optionValue) => {
          optionValue.selected = false
        })
      })
    }
    const newItems = this.context.state.items.filter((cartItem: ICartItem) => cartItem.id !== item.id)
    if (this.context.state.currentItem) {
      if (this.context.state.currentItem.id === item.id) {
        this.context.commit('CURRENT_ITEM', undefined)
      }
    }
    this.context.commit('SET_ITEMS', newItems)
  }

  @Action
  public removeOption (option: GlassOption) {
    option.values.forEach((optionValue) => {
      optionValue.selected = false
    })
  }

  @Action
  public selectGlassType (type: GlassType) {
    const cartItem = this.context.state.currentItem
    if (cartItem !== undefined) {
      cartItem.type = type
      cartItem.options = []
    }
    this.context.commit('CURRENT_ITEM', cartItem)
    return this.context.dispatch('setDneye', cartItem.dneye)
  }

  @Action({ rawError: true })
  public setDneye (choice: boolean) {
    const cartItem = this.context.state.currentItem
    if (cartItem !== undefined && cartItem.type !== undefined) {
      cartItem.dneye = choice
      const dneyeOption = cartItem.type.options.find((option: GlassOption) => option.optionId === 5)
      if (dneyeOption) {
        dneyeOption.values = dneyeOption.values.map((optionValue: GlassOptionValue) => {
          optionValue.selected = choice
          return optionValue
        })
      }
      this.context.commit('CURRENT_ITEM', cartItem)
    }
  }

  @Action
  public setImpressionist (choice: boolean) {
    const cartItem = this.context.state.currentItem
    if (cartItem !== undefined) {
      cartItem.impressionist = choice
    }
    this.context.commit('CURRENT_ITEM', cartItem)
  }

  @Action
  public forceUpdate () {
    const cartItem = this.context.state.currentItem
    const items = this.context.state.items
    this.context.commit('CURRENT_ITEM', cartItem)
  }

  @Action({ rawError: true })
  public toggleOptionValue (optionValue: GlassOptionValue, selected?: boolean | undefined) {
    if (this.context.state.currentItem && this.context.state.currentItem.type) {
      const glassesOption = this.context.state.currentItem.type.options
        .find((option: GlassOption) => (option.id === optionValue.optionId))
      if (glassesOption) {
        glassesOption.values = glassesOption.values.map((val: GlassOptionValue) => {
          if (!optionValue.isExtra && !optionValue.selected && val.id !== optionValue.id) {
            val.selected = false
          }
          return val
        })
        const value = glassesOption.values.find((val: GlassOptionValue) => val.id === optionValue.id)
        value.selected = !value.selected
      }
    }
  }

  @Mutation
  private ADD_ITEM (item: ICartItem) {
    this.items.push(item)
  }

  @Mutation
  private CURRENT_ITEM (item: ICartItem | undefined) {
    this.currentItem = item
    // change indexes
    const sorted = this.items.sort((a, b) => (a.price < b.price) ? 1 : ((b.price < a.price) ? -1 : 0))
    sorted.forEach((cartItem: ICartItem, index: number) => {
      cartItem.index = index
    })
  }

  @Mutation
  private REMOVE_ITEM (id: string) {
    this.items = this.items.filter((item: ICartItem) => item.id !== id)
  }

  @Mutation
  private SET_ITEMS (items: ICartItem[]) {
    this.items = items
  }
}

export default Cart
